import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Direct import: the @framework/services barrel pulls AWS/Amplify into the phone page.
import { BaseHttpService } from '@framework/services/base-http.service';
import {
  PhoneUploadContext,
  PhoneUploadFile,
  PhoneUploadMobileSession,
  PhoneUploadSession,
  PhoneUploadSessionCreated
} from './phone-upload.models';

const OWNER_HEADER = 'X-Phone-Upload-Owner';
const MOBILE_HEADER = 'X-Phone-Upload-Token';

/**
 * TransferHttpCacheModule (AppModule) answers repeat GETs to the same URL from
 * memory until the app is "stable", which a page that keeps polling never is.
 * A unique URL per request makes every status check reach the server.
 */
function cacheBust(): { [param: string]: string } {
  return { _: String(Date.now()) };
}

/**
 * Upload-from-phone sessions. The desktop holds the owner token; the phone only
 * ever sees the mobile token from the QR code. See docs/phone-upload/backend-spec.md.
 */
@Injectable({ providedIn: 'root' })
export class PhoneUploadSessionHttpService extends BaseHttpService {

  constructor(httpClient: HttpClient) {
    super(httpClient, '/phone-upload-sessions');
  }

  public create(context: PhoneUploadContext): Observable<PhoneUploadSessionCreated> {
    return this.post('/', {
      company_id: context.companyId,
      story_id: context.storyId || null,
      context: context.context,
      max_files: context.maxFiles,
      accept: context.accept
    }).pipe(map((response: any) => response as PhoneUploadSessionCreated));
  }

  public getSession(id: string, ownerToken: string) {
    return this.getWithHeaders<PhoneUploadSession>(`/${id}/`, { [OWNER_HEADER]: ownerToken }, cacheBust());
  }

  public cancel(id: string, ownerToken: string) {
    return this.post(`/${id}/cancel/`, {}, { headers: { [OWNER_HEADER]: ownerToken } });
  }

  public getMobileSession(id: string, mobileToken: string) {
    return this.getWithHeaders<PhoneUploadMobileSession>(`/${id}/mobile/`, { [MOBILE_HEADER]: mobileToken }, cacheBust());
  }

  public addFile(id: string, mobileToken: string, uuid: string): Observable<PhoneUploadFile> {
    return this.post(`/${id}/files/`, { uuid }, { headers: { [MOBILE_HEADER]: mobileToken } })
      .pipe(map((response: any) => response as PhoneUploadFile));
  }
}
